import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone, User, MapPin, Navigation, MessageSquare, 
  CreditCard, Banknote, Search, Clock, X, Check,
  History, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuickBookingForm = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    pickup: '',
    dropoff: '',
    notes: '',
    payment_method: 'cash',
    scheduled_time: ''
  });
  const [loading, setLoading] = useState(false);
  const [passengerHistory, setPassengerHistory] = useState(null);
  const [searchingPassenger, setSearchingPassenger] = useState(false);
  const phoneInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, [isOpen]);

  // Auto-search passenger by phone
  useEffect(() => {
    const searchPassenger = async () => {
      if (formData.phone.length >= 9) {
        setSearchingPassenger(true);
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/passengers/search?phone=${formData.phone}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data && data.length > 0) {
              const passenger = data[0];
              setPassengerHistory(passenger);
              setFormData(prev => ({
                ...prev,
                name: prev.name || passenger.name || '',
              }));
            } else {
              setPassengerHistory(null);
            }
          }
        } catch (error) {
          console.error('Error searching passenger:', error);
        } finally {
          setSearchingPassenger(false);
        }
      } else {
        setPassengerHistory(null);
      }
    };

    const debounce = setTimeout(searchPassenger, 500);
    return () => clearTimeout(debounce);
  }, [formData.phone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phone || !formData.pickup) {
      toast.error('Numri dhe pickup janë të detyrueshme');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const bookingData = {
        passenger_phone: formData.phone,
        passenger_name: formData.name || 'Pasagjer',
        pickup_address: formData.pickup,
        dropoff_address: formData.dropoff || null,
        notes: formData.notes || null,
        payment_method: formData.payment_method,
        scheduled_time: formData.scheduled_time || null,
        source: 'dispatcher'
      };

      const response = await fetch(`${API_URL}/api/rides`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Porosia u krijua me sukses!');
        
        // Reset form
        setFormData({
          phone: '',
          name: '',
          pickup: '',
          dropoff: '',
          notes: '',
          payment_method: 'cash',
          scheduled_time: ''
        });
        setPassengerHistory(null);
        
        if (onSuccess) onSuccess(data);
        onClose();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Gabim në krijimin e porosisë');
      }
    } catch (error) {
      toast.error('Gabim në lidhje me serverin');
    } finally {
      setLoading(false);
    }
  };

  const applyHistoryAddress = (address) => {
    setFormData(prev => ({ ...prev, pickup: address }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-bold">Porosi e Re</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numri i Telefonit *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={phoneInputRef}
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+355 6X XXX XXXX"
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
              {searchingPassenger && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Passenger History */}
          {passengerHistory && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Klient ekzistues</span>
              </div>
              <p className="text-sm font-medium">{passengerHistory.name}</p>
              <p className="text-xs text-gray-500">
                {passengerHistory.total_rides || 0} udhëtime të mëparshme
              </p>
              
              {/* Last addresses */}
              {passengerHistory.recent_addresses && passengerHistory.recent_addresses.length > 0 && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <p className="text-xs font-medium text-green-700 mb-1">Adresa të fundit:</p>
                  <div className="space-y-1">
                    {passengerHistory.recent_addresses.slice(0, 3).map((addr, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => applyHistoryAddress(addr)}
                        className="w-full text-left text-xs p-2 bg-white rounded-lg hover:bg-green-100 flex items-center gap-2"
                      >
                        <History className="w-3 h-3 text-gray-400" />
                        <span className="truncate">{addr}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Emri (opsionale)
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Emri i klientit"
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Pickup */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nisja (Pickup) *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
              <input
                type="text"
                value={formData.pickup}
                onChange={(e) => setFormData({ ...formData, pickup: e.target.value })}
                placeholder="Adresa e nisjes"
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Dropoff */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mbërritja (Drop) - opsionale
            </label>
            <div className="relative">
              <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
              <input
                type="text"
                value={formData.dropoff}
                onChange={(e) => setFormData({ ...formData, dropoff: e.target.value })}
                placeholder="Adresa e mbërritjes"
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shënime
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="p.sh. me valixhe, para hyrjes, etj."
                rows={2}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Metoda e Pagesës
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, payment_method: 'cash' })}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition ${
                  formData.payment_method === 'cash'
                    ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Banknote className="w-5 h-5" />
                <span className="font-medium">Cash</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, payment_method: 'pos' })}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition ${
                  formData.payment_method === 'pos'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <CreditCard className="w-5 h-5" />
                <span className="font-medium">POS (Zyrë)</span>
              </button>
            </div>
          </div>

          {/* Scheduled Time (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Porosi e planifikuar (opsionale)
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="datetime-local"
                value={formData.scheduled_time}
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-200 rounded-xl font-medium hover:bg-gray-100"
            >
              Anulo
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.phone || !formData.pickup}
              className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Krijo Porosi
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickBookingForm;
